/**
 * Case Management Enums
 *
 * Enums for case management domain.
 * Issue #313 - Centralize enums to shared-types
 */

import { z } from 'zod';

// ============================================================================
// CASE STATUS
// ============================================================================

/**
 * Case lifecycle status
 */
export enum CaseStatus {
  NEW = 'new',
  IN_PROGRESS = 'in_progress',
  PENDING_REVIEW = 'pending_review',
  UNDER_REVIEW = 'under_review',
  PENDING_APPROVAL = 'pending_approval',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  CLOSED = 'closed',
  CANCELLED = 'cancelled',
  ARCHIVED = 'archived',
}

/** Zod schema for CaseStatus validation */
export const CaseStatusSchema = z.nativeEnum(CaseStatus);

/** Array of all case status values for iteration */
export const CASE_STATUS_VALUES = Object.values(CaseStatus);

// ============================================================================
// CASE PRIORITY
// ============================================================================

/**
 * Case priority levels
 *
 * Note: CRITICAL is included for compatibility with security severity contexts.
 * For case management, URGENT is typically the highest level.
 * For security/risk contexts, consider using RecommendationSeverity instead.
 */
export enum CasePriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent',
  CRITICAL = 'critical',
}

/** Zod schema for CasePriority validation */
export const CasePrioritySchema = z.nativeEnum(CasePriority);

/** Array of all case priorities for iteration */
export const CASE_PRIORITIES = Object.values(CasePriority);

// ============================================================================
// CASE SLA STATUS
// ============================================================================

/**
 * Case-level SLA status DERIVED at the API gateway (see
 * `2-api-gateway/src/trpc/routers/home.router.ts:gatherSignals`) from
 * `CaseResponse.due_date` — case-management-service does NOT project a
 * `sla_status` field on `CaseResponse`, so consumers must rely on the
 * gateway-side derivation rather than reading the field straight off
 * the gRPC wire. Distinct from `SLAComplianceStatus` in `sla.enums.ts`
 * which is used by the dedicated SLA-tracking surface (uppercase
 * `ON_TRACK` / `BREACHED` / etc.). The lowercase values here mirror
 * what the home/dashboard signals serialize to the portal.
 */
export enum CaseSlaStatus {
  AT_RISK = 'at_risk',
  BREACHED = 'breached',
  WARNING = 'warning',
  ON_TRACK = 'on_track',
}

/** Zod schema for CaseSlaStatus validation */
export const CaseSlaStatusSchema = z.nativeEnum(CaseSlaStatus);

/** Array of all case SLA statuses for iteration */
export const CASE_SLA_STATUSES = Object.values(CaseSlaStatus);

// ============================================================================
// AI RECOMMENDATION
// ============================================================================

/**
 * AI recommendation for case review
 */
export enum AIRecommendation {
  APPROVE = 'approve',
  REJECT = 'reject',
  REVIEW = 'review',
}

/** Zod schema for AIRecommendation validation */
export const AIRecommendationSchema = z.nativeEnum(AIRecommendation);

/** Array of all AI recommendations for iteration */
export const AI_RECOMMENDATIONS = Object.values(AIRecommendation);

// ============================================================================
// CASE DOCUMENT STATUS
// ============================================================================

/**
 * Case document verification status
 */
export enum CaseDocumentStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  VERIFIED = 'verified',
  REJECTED = 'rejected',
}

/** Zod schema for CaseDocumentStatus validation */
export const CaseDocumentStatusSchema = z.nativeEnum(CaseDocumentStatus);

/** Array of all case document statuses for iteration */
export const CASE_DOCUMENT_STATUSES = Object.values(CaseDocumentStatus);

// ============================================================================
// ACTOR TYPE
// ============================================================================

/**
 * Actor type for timeline events and actions
 */
export enum ActorType {
  CONSUMER = 'consumer',
  USER = 'user',
  SYSTEM = 'system',
}

/** Zod schema for ActorType validation */
export const ActorTypeSchema = z.nativeEnum(ActorType);

/** Array of all actor types for iteration */
export const ACTOR_TYPES = Object.values(ActorType);
